// frontend/src/utils/frame/buildActiveFrameState.js
// Owns: the canonical, observable conversational frame assembled from existing runtime states.
// Does not generate responses, mutate memory, or authorize frame transitions.

function normalizeName(entity = null) {
  return String(
    entity?.name ||
      [entity?.first_name || entity?.firstName, entity?.last_name || entity?.lastName]
        .filter(Boolean)
        .join(' ')
  )
    .trim()
    .toLowerCase();
}

function getEntityIdentityKey(entity = null) {
  const entityId = String(entity?.entityId || '').trim();
  if (entityId) return `id:${entityId}`;

  const name = normalizeName(entity);
  return name ? `name:${name}` : '';
}

function sameEntity(left = null, right = null) {
  const leftId = String(left?.entityId || '').trim();
  const rightId = String(right?.entityId || '').trim();

  if (leftId && rightId) return leftId === rightId;
  return normalizeName(left) === normalizeName(right);
}

function uniqueEntities(entities = []) {
  const seen = new Set();

  return (Array.isArray(entities) ? entities : []).filter((entity) => {
    const key = getEntityIdentityKey(entity);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getPendingQuestion(
  pendingQuestionState = null,
  pendingQuestion = null
) {
  return (
    pendingQuestionState?.pendingQuestion ||
    pendingQuestionState?.nextPendingQuestion ||
    pendingQuestion ||
    null
  );
}

function hasMeaningfulPrecedingSegment(segment = '') {
  const ignoredTokens = new Set([
    'a',
    'also',
    'and',
    'anyway',
    'but',
    'by',
    'can',
    'could',
    'did',
    'do',
    'hey',
    'now',
    'oh',
    'please',
    'so',
    'the',
    'then',
    'way',
    'well',
    'will',
    'would',
    'you',
  ]);

  const meaningfulTokens = String(segment || '')
    .toLowerCase()
    .match(/[a-z0-9']+/g)
    ?.filter((token) => !ignoredTokens.has(token));

  return Boolean(meaningfulTokens?.length);
}

function buildTurnOperations({
  relationshipReferenceState = null,
  pendingQuestionState = null,
  pendingQuestion = null,
} = {}) {
  const activePendingQuestion = getPendingQuestion(
    pendingQuestionState,
    pendingQuestion
  );
  const requestBoundary = relationshipReferenceState?.requestBoundary || null;
  const precedingSegment = requestBoundary?.precedingSegment || '';
  const requestSegment = requestBoundary?.requestSegment || '';
  const activeRequestTarget = relationshipReferenceState?.primaryEntity || null;
  const requestedRole =
    relationshipReferenceState?.requestedRole ||
    relationshipReferenceState?.targetRole ||
    null;

  const pendingAnswerCandidate = Boolean(
    activePendingQuestion &&
      requestSegment &&
      hasMeaningfulPrecedingSegment(precedingSegment)
  );

  const pendingAnswerTarget = pendingAnswerCandidate
    ? activePendingQuestion?.targetEntity || null
    : null;

  const operations = [];

  if (pendingAnswerCandidate) {
    operations.push({
      type: 'pending_answer_candidate',
      segment: precedingSegment,
      targetEntity: pendingAnswerTarget,
      pendingQuestionText: activePendingQuestion?.text || null,
      authority: 'observed',
    });
  }

  if (requestSegment && requestedRole) {
    operations.push({
      type: 'relationship_recall_request',
      segment: requestSegment,
      requestedRole,
      targetEntity: activeRequestTarget,
      resolved: Boolean(activeRequestTarget),
      authority: 'user_request',
    });
  }

  return {
    compound: operations.length > 1,
    pendingAnswerCandidate,
    pendingAnswerTarget,
    activeRequestTarget,
    requestedRole,
    requestBoundary,
    operations,
  };
}

function buildEvidence({
  relationshipReferenceState = null,
  turnOperations = null,
  turnAttachment = null,
  pronounResolutionSignal = null,
} = {}) {
  const evidence = [];

  if (turnOperations?.pendingAnswerCandidate) {
    evidence.push({
      type: 'pending_answer_segment',
      target: turnOperations.pendingAnswerTarget?.name || null,
      segment: turnOperations.requestBoundary?.precedingSegment || null,
    });
  }

  if (relationshipReferenceState?.requestedRole) {
    evidence.push({
      type: 'relationship_request_role',
      role: relationshipReferenceState.requestedRole,
      resolvedTarget: relationshipReferenceState.primaryEntity?.name || null,
      resolved: relationshipReferenceState.resolved === true,
    });
  }

  if (relationshipReferenceState?.primaryEntity) {
    evidence.push({
      type: 'relationship_path',
      target: relationshipReferenceState.primaryEntity.name || null,
      role: relationshipReferenceState.targetRole || null,
      pathType:
        relationshipReferenceState.primaryEntity?.metadata?.pathType || null,
    });
  }

  if (turnAttachment?.attached === true) {
    evidence.push({
      type: 'turn_attachment',
      anchorType: turnAttachment.anchorType || null,
      turnAct: turnAttachment.turnAct || null,
    });
  }

  if (pronounResolutionSignal?.resolved === true) {
    evidence.push({
      type: 'pronoun_resolution',
      target: pronounResolutionSignal?.candidateEntity?.name || null,
    });
  }

  return evidence;
}

function buildContradictions(relationshipReferenceState = null) {
  if (
    relationshipReferenceState?.evidence?.preventedNestedTargetSubstitution !== true
  ) {
    return [];
  }

  return [
    {
      type: 'requested_relationship_unresolved',
      requestedRole: relationshipReferenceState.requestedRole || null,
      nestedResolvedCount:
        relationshipReferenceState?.evidence?.nestedResolvedCount || 0,
      protectedBoundary: 'nested_relationship_not_promoted',
    },
  ];
}

function buildActiveFrameState({
  message = '',
  frame = null,
  control = null,
  objectiveDecisionState = null,
  entityState = null,
  relationshipReferenceState = null,
  reference = null,
  topicState = null,
  turnAttachment = null,
  pendingQuestionState = null,
  pendingQuestion = null,
  pronounResolutionSignal = null,
  lifecycle = null,
} = {}) {
  const turnOperations = buildTurnOperations({
    relationshipReferenceState,
    pendingQuestionState,
    pendingQuestion,
  });

  const hasActiveRelationshipRequest = Boolean(turnOperations.requestedRole);

  const primaryEntity =
    turnOperations.activeRequestTarget ||
    (hasActiveRelationshipRequest
      ? null
      : reference?.lastReferencedEntity || entityState?.active || null);

  const supportingEntities = uniqueEntities([
    ...(relationshipReferenceState?.supportingEntities || []),
    turnOperations.pendingAnswerTarget,
    ...(Array.isArray(entityState?.named) ? entityState.named : []),
    ...(Array.isArray(entityState?.related) ? entityState.related : []),
  ]).filter((entity) => !sameEntity(entity, primaryEntity));

  return {
    governingFrame: frame || null,
    workingFrame: {
      frame: frame || null,
      layer: control?.frameLayer || 'execution',
      authority: control?.authority || 'ambient',
    },
    sidebarFrames: [],
    objective:
      objectiveDecisionState?.activeObjective ||
      objectiveDecisionState?.objective ||
      null,
    situation: {
      inboundMessage: String(message || '').trim(),
      topic: topicState?.lastReferencedTopic || reference?.lastReferencedTopic || null,
      thread: topicState?.lastReferencedThread || reference?.lastReferencedThread || null,
      turnOperations,
    },
    primaryEntity,
    supportingEntities,
    references: {
      lastReferencedEntity: reference?.lastReferencedEntity || null,
      lastReferencedTopic: reference?.lastReferencedTopic || null,
      lastReferencedThread: reference?.lastReferencedThread || null,
    },
    temporalContext: null,
    evidence: buildEvidence({
      relationshipReferenceState,
      turnOperations,
      turnAttachment,
      pronounResolutionSignal,
    }),
    contradictions: buildContradictions(relationshipReferenceState),
    lifecycle: lifecycle || null,
  };
}

export { buildActiveFrameState };
export default buildActiveFrameState;
